import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import classNames from 'classnames';

import { useOutsideClick } from '../../hooks';
import { ComboBoxOption, DEFAULT_MAX_RESULTS_COUNT, SearchInputTestID } from './constants';
import ErrorMessage from './ErrorMessage/ErrorMessage';
import LoadingResults from './LoadingResults/LoadingResults';
import NoResultsFound from './NoResultsFound/NoResultsFound';
import SearchResultsList from './SearchResultsList/SearchResultsList';
import SelectedOptionBtn from './SelectedOptionBtn/SelectedOptionBtn';

export type Props = {
  errorMessage?: string;
  // If no options are passed in and the component is loading, a loading state will render.
  loading?: boolean;
  // Message to render when possible options are being fetched.
  loadingMessage?: string;
  // Maximum number of options presented at once.
  maxResultsCount?: number;
  notFoundText?: string;
  onInputChange: (searchTerm: string) => void;
  onSelect: (selectedOption: any) => void;
  placeholder?: string;
  selectableOptions: ComboBoxOption[];
  // Option for allowing text and subtext on two lines
  stackSubText?: boolean;
  startingValue?: string;
};

const ComboBox: React.FC<Props> = ({
  errorMessage,
  loading = false,
  loadingMessage,
  maxResultsCount = DEFAULT_MAX_RESULTS_COUNT,
  notFoundText,
  onInputChange,
  onSelect,
  placeholder,
  selectableOptions,
  stackSubText,
  startingValue,
}) => {
  const [isInputting, setIsInputting] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputWidth, setInputWidth] = useState(0);
  const [selectedOption, setSelectedOption] = useState<ComboBoxOption>();

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    onInputChange(event.target.value);
  };

  const handleSelect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const matchingOption = selectableOptions.find(
      (selectableOption) => selectableOption.key === event.currentTarget.value,
    );
    setIsInputting(false);
    setSearchTerm(event.currentTarget.value);
    setSelectedOption(matchingOption);
    onSelect(matchingOption);
  };

  useEffect(() => {
    if (inputRef.current?.clientWidth) {
      setInputWidth(inputRef.current.clientWidth);
    }
  }, [inputRef]);

  useOutsideClick(containerRef, () => {
    if (selectedOption) {
      setIsInputting(false);
    }
  });

  const noResultsFound = searchTerm && selectableOptions.length === 0;
  const hasSearchResults = (!!searchTerm || !!startingValue) && selectableOptions.length > 0;
  const hasErrorMessage = !isEmpty(errorMessage);

  const inputClassName = classNames(
    'text-sm',
    'w-full',
    'p-2',
    'pl-3',
    'font-medium',
    'text-gray-800',
    'bg-white',
    'rounded-md',
    'border',
    'hover:border-blue-400',
    'border-gray-300',
    'focus:rounded-md',
    'outline-color-blue',
  );

  return (
    <div id="combo-box-input" ref={containerRef} className={noResultsFound ? 'relative' : ''}>
      {isInputting && (
        <>
          <input
            className={inputClassName}
            data-testid={SearchInputTestID}
            defaultValue={selectedOption?.displayText || startingValue || ''}
            list="options"
            onChange={handleInputChange}
            placeholder={placeholder}
            ref={inputRef}
          />

          {!loading && noResultsFound && (
            <NoResultsFound inputWidth={inputWidth} message={notFoundText} />
          )}

          {loading && noResultsFound && (
            <LoadingResults inputWidth={inputWidth} message={loadingMessage} />
          )}

          {hasSearchResults && (
            <SearchResultsList
              inputWidth={inputWidth}
              maxResultsCount={maxResultsCount}
              onClick={handleSelect}
              selectableOptions={selectableOptions}
              stackSubText={stackSubText}
            />
          )}
        </>
      )}

      {!isInputting && selectedOption && (
        <>
          <SelectedOptionBtn
            hasErrorState={hasErrorMessage}
            onClick={() => setIsInputting(true)}
            selectedOption={selectedOption}
          />

          {hasErrorMessage && <ErrorMessage message={errorMessage} />}
        </>
      )}
    </div>
  );
};

export default ComboBox;
